import React, { useEffect, useRef, useState } from 'react';
import { animated } from 'react-spring';
// Voice recording now provided by useTaskHandlers
import type { TaskItem } from '../../../../types/topic';
import { MobileTaskHeader } from './MobileTaskHeader';
import { MobileTaskColumn } from './MobileTaskColumn';
// Migrated from useTaskState to taskUIStore for unified state management
import { useReorderTask, useAvailableTags } from '../../../../hooks/queries/useTasks';
import { useTaskHandlers } from '../hooks/useTaskHandlers';
import { useTaskUIStore } from '../../../../store/taskUIStore';
import { useTaskColumnSwipe } from '../hooks/useTaskColumnSwipe';
import { useColumnActions } from '../dnd/useColumnActions';
import { logger } from '../../../../utils/logger';
import { useTaskColumns } from '../../../../hooks/queries/useTaskColumns';
import { AlertCircle, Loader2 } from 'lucide-react';
import TaskStatusFilterDropdown from '../filters/TaskStatusFilterDropdown';
import TaskAssigneeFilterDropdown from '../filters/TaskAssigneeFilterDropdown';
import { MobileTaskStatusFilter, MobileTaskUserFilter } from './MobileFilterDropdowns';
import { UserFilter } from '../../../dashboard/types/dashboardTypes';
import { DeleteColumnConfirm } from '../ColumnMenu';
import { useStatusBarInfo } from '../../../../hooks/useStatusBarInfo';

interface MobileTaskBoardProps {
  topicName: string;
  topicId: string;
  tasks: TaskItem[];
  onBackClick: () => void;
  projectId?: string;
  hasCollaborators?: boolean;
  userFilters?: UserFilter[];
  toggleUserFilter?: (userId: string, isExclusive?: boolean) => void;
}

export function MobileTaskBoard({
  topicName,
  topicId,
  tasks,
  onBackClick,
  hasCollaborators = false,
  userFilters = [],
  toggleUserFilter,
}: MobileTaskBoardProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Voice recording now provided by useTaskHandlers

  // State for filter dropdowns
  const [isTaskFilterOpen, setIsTaskFilterOpen] = useState(false);
  const [isUserFilterOpen, setIsUserFilterOpen] = useState(false);
  const [showSearchInput, setShowSearchInput] = useState(false);

  // State for header shadow
  const [isScrolled, setIsScrolled] = useState(false);

  // Task state and actions from unified taskUIStore
  const {
    isCreating,
    showDescription,
    newTaskTitle,
    newTaskDescription,
    newTaskTags,
    newTaskDeadline,
    newTaskAssignedTo,
    activeColumn,
    mobileActiveColumn,
    itemToDelete,
    columnToDelete,
    setActiveColumn,
    setMobileActiveColumn,
    setIsCreating,
    setNewTaskTitle,
    setNewTaskDescription,
    setItemToDelete,
    setIsEditingColumnName,
    setColumnToDelete,
    resetTaskForm: resetTaskFormState,
  } = useTaskUIStore();

  // Add scroll listener to detect when to show header shadow
  React.useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const scrollTop = contentRef.current.scrollTop;
        setIsScrolled(scrollTop > 10);
      }
    };

    const scrollContainer = contentRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Get columns using React Query (must be before useColumnActions which depends on columns)
  const { data: columns = [], isLoading, isError } = useTaskColumns(topicId);

  // Add column actions hook
  // Note: tasks prop is already filtered by parent TaskTopic component

  const columnActions = useColumnActions({
    topicId,
    columns,
    setIsEditingColumnName,
    setColumnToDelete,
  });

  // Shared task handlers (consistent with desktop)
  const {
    handleTaskDeletion,
    handleTaskUpdate,
    handleTaskCreation,
    handleVoiceInput,
    handleTaskMoveError,
    isRecording,
  } = useTaskHandlers(topicId);

  // Keep reorder mutation for mobile-specific reordering functionality
  const reorderTaskMutation = useReorderTask();

  // Set the active topic ID in the store for task creation
  useEffect(() => {
    useTaskUIStore.setState({ activeTopicId: topicId });
  }, [topicId]);

  // Task deletion handler now provided by useTaskHandlers

  // Task update handler now provided by useTaskHandlers

  // Handle task reordering with React Query - replacement for taskStore reordering
  const handleReorderTask = async (taskId: string, direction: 'up' | 'down' | 'top') => {
    try {
      // Find the task being moved
      const currentTask = tasks.find(t => t.id === taskId);
      if (!currentTask) {
        logger.error('Task not found for reordering:', taskId);
        return;
      }

      // Get all tasks in the same status, sorted by position
      const tasksInColumn = tasks
        .filter(t => t.status === currentTask.status)
        .sort((a, b) => (a.position || 0) - (b.position || 0));

      // Find current index
      const currentIndex = tasksInColumn.findIndex(t => t.id === taskId);
      if (currentIndex === -1) {
        logger.error('Task not found in column for reordering:', taskId);
        return;
      }

      // Calculate target index based on direction
      let newIndex: number;
      if (direction === 'top') {
        newIndex = 0;
      } else if (direction === 'up') {
        newIndex = Math.max(0, currentIndex - 1);
      } else {
        // direction === 'down'
        newIndex = Math.min(tasksInColumn.length - 1, currentIndex + 1);
      }

      // Don't move if already at target position
      if (newIndex === currentIndex) {
        return;
      }

      logger.component('Reordering task with React Query', {
        taskId,
        direction,
        currentIndex,
        newIndex,
        status: currentTask.status,
      });

      await reorderTaskMutation.mutateAsync({
        topicId,
        taskId,
        newStatus: currentTask.status, // Keep same status since this is just reordering within column
        newIndex,
      });

      logger.component('Task reordered successfully');
    } catch (error) {
      handleTaskMoveError(error, {
        taskId,
        topicId,
        fromColumn: currentTask.status,
        toColumn: currentTask.status,
      });
    }
  };

  // Voice input handler now provided by useTaskHandlers

  // Now using the unified createTask from useTaskCreation hook

  const handleSubmit = async (columnId: TaskItem['status']) => {
    try {
      // Get the current form data from taskUIStore
      const currentTitle = newTaskTitle;

      // Validate title exists
      if (!currentTitle?.trim()) {
        logger.error('Cannot submit task with empty title from component state');
        return;
      }

      // Use shared task creation handler
      await handleTaskCreation(columnId, {
        title: currentTitle,
        description: newTaskDescription,
        tags: newTaskTags,
        deadline: newTaskDeadline,
        assignedTo: newTaskAssignedTo,
      });

      // Clear mobile form state after successful creation
      resetTaskFormState();

      // Close the form
      setIsCreating(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    } catch (error) {
      // Error handling is now managed by the shared handler
      logger.error('Unexpected error in mobile form submission:', error);
    }
  };

  // Sort columns by position for navigation (columns already fetched at top of component)
  const sortedColumns = React.useMemo(() => {
    return [...columns].sort((a, b) => a.position - b.position);
  }, [columns]);

  // Extract ordered column IDs for swipe navigation
  const orderedColumnIds = React.useMemo(() => {
    return sortedColumns.map(col => col.status);
  }, [sortedColumns]);

  // Set up swipe navigation
  const { bind, x } = useTaskColumnSwipe({
    columnIds: orderedColumnIds,
    activeColumnId: mobileActiveColumn,
    onColumnChange: columnId => {
      setMobileActiveColumn(columnId);
    },
    onBackToTopics: onBackClick,
    threshold: 0.15,
  });

  // Get filter state and methods from UI store
  const {
    showUnscheduledOnly,
    showUnassignedOnly,
    showHighlightedOnly,
    selectedTags,
    toggleUnscheduledOnly,
    toggleUnassignedOnly,
    toggleHighlightedOnly,
    setSelectedTags,
  } = useTaskUIStore();

  // Get available tags for this topic using React Query
  const availableTags = useAvailableTags(topicId);

  // Handle filter dropdowns
  const handleOpenTaskFilters = () => {
    setIsTaskFilterOpen(true);
    setIsUserFilterOpen(false);
  };

  const handleOpenUserFilters = () => {
    setIsUserFilterOpen(true);
    setIsTaskFilterOpen(false);
  };

  // Toggle filter handlers using taskUIStore
  const handleToggleUnscheduledOnly = () => {
    toggleUnscheduledOnly();
  };

  const handleToggleUnassignedOnly = () => {
    toggleUnassignedOnly();
  };

  const handleToggleHighlightedOnly = () => {
    toggleHighlightedOnly();
  };

  const handleTagsChange = (tags: string[]) => {
    setSelectedTags(tags);
  };

  const handleColumnSelect = (columnId: string) => {
    setMobileActiveColumn(columnId);
  };

  // Import the status bar hook at the top of the file
  const { isNativeApp, statusBarHeight, navigationBarHeight } = useStatusBarInfo();

  // Log status bar info for debugging
  React.useEffect(() => {
    if (isNativeApp) {
      logger.capacitor('MobileTaskBoard: Using status bar height', {
        statusBarHeight,
        navigationBarHeight,
      });
    }
  }, [isNativeApp, statusBarHeight, navigationBarHeight]);

  return (
    <div className="flex flex-col h-screen overflow-hidden fixed inset-0 bg-[var(--content-bg-color)] mobile-task-board">
      {/* Fixed header with shadow on scroll, and dynamic status bar padding */}
      <div
        className={`sticky top-0 z-30 transition-shadow duration-200 bg-[var(--content-bg-color)] mobile-task-header ${isScrolled ? 'shadow-md' : ''}`}
        style={{
          paddingTop: isNativeApp ? `${statusBarHeight}px` : '0',
        }}
      >
        <MobileTaskHeader
          topicName={topicName}
          onBackClick={onBackClick}
          isRecording={isRecording}
          onVoiceClick={() => handleVoiceInput(mobileActiveColumn)}
          onAddClick={() => {
            // Use the shared state reset function to ensure a clean form
            resetTaskFormState();
            // Then set the necessary state for the new form
            setActiveColumn(mobileActiveColumn);
            setIsCreating(true);
            setTimeout(() => inputRef.current?.focus(), 100);
          }}
          columns={columns.map(col => ({
            id: col.status,
            label: col.name,
            color: col.color,
          }))}
          activeColumn={mobileActiveColumn}
          onColumnSelect={handleColumnSelect}
          // Pass all filter states directly from the store
          showUnscheduledOnly={showUnscheduledOnly}
          showUnassignedOnly={showUnassignedOnly}
          showHighlightedOnly={showHighlightedOnly}
          userFilters={userFilters}
          hasCollaborators={hasCollaborators}
          onOpenTaskFilters={handleOpenTaskFilters}
          onOpenUserFilters={userFilters.length > 0 ? handleOpenUserFilters : undefined}
          // Column management handlers
          onDeleteColumnClick={columnId => setColumnToDelete(columnId)}
          onEditColumnNameClick={columnId => setIsEditingColumnName(columnId)}
          onColorSelect={columnActions.handleColumnColorChange}
          onAddColumnAfter={columnActions.handleAddColumnAfter}
          onMoveColumnLeft={columnActions.handleMoveColumnLeft}
          onMoveColumnRight={columnActions.handleMoveColumnRight}
          // Search toggle handler
          onToggleSearch={() => setShowSearchInput(!showSearchInput)}
          // Pass topicId for filter persistence
          topicId={topicId}
        />
      </div>

      {/* Mobile filter modals using the new components */}
      <MobileTaskStatusFilter
        isOpen={isTaskFilterOpen}
        onClose={() => setIsTaskFilterOpen(false)}
        showUnscheduledOnly={showUnscheduledOnly}
        toggleShowUnscheduledOnly={handleToggleUnscheduledOnly}
        showUnassignedOnly={showUnassignedOnly}
        toggleShowUnassignedOnly={handleToggleUnassignedOnly}
        showHighlightedOnly={showHighlightedOnly}
        toggleShowHighlightedOnly={handleToggleHighlightedOnly}
        hasCollaborators={hasCollaborators}
        availableTags={availableTags}
        selectedTags={selectedTags}
        onTagsChange={handleTagsChange}
      />

      {userFilters.length > 0 && (
        <MobileTaskUserFilter
          isOpen={isUserFilterOpen}
          onClose={() => setIsUserFilterOpen(false)}
          userFilters={userFilters}
          toggleUserFilter={toggleUserFilter}
        />
      )}

      {/* Hidden original dropdowns to maintain the component structure */}
      <div className="hidden">
        <TaskStatusFilterDropdown
          showUnscheduledOnly={showUnscheduledOnly}
          toggleShowUnscheduledOnly={handleToggleUnscheduledOnly}
          showUnassignedOnly={showUnassignedOnly}
          toggleShowUnassignedOnly={handleToggleUnassignedOnly}
          showHighlightedOnly={showHighlightedOnly}
          toggleShowHighlightedOnly={handleToggleHighlightedOnly}
          isOpen={false}
          onToggle={() => {}}
          isMobile={true}
          hasCollaborators={hasCollaborators}
          availableTags={availableTags}
          selectedTags={selectedTags}
          onTagsChange={handleTagsChange}
        />
      </div>

      <div className="hidden">
        <TaskAssigneeFilterDropdown
          userFilters={userFilters}
          isOpen={false}
          onToggle={() => {}}
          onSelect={toggleUserFilter}
          isMobile={true}
        />
      </div>

      {/* Column deletion confirmation dialog */}
      <DeleteColumnConfirm
        columnId={columnToDelete}
        columns={columns}
        onConfirm={() => columnActions.handleDeleteColumn(columnToDelete)}
        onCancel={() => setColumnToDelete(null)}
      />

      {/* Scrollable content area with swipe gestures - adjusted height for bottom nav bar */}
      {isLoading && !columns.length ? (
        <div className="flex-1 bg-gray-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : isError ? (
        <div className="flex-1 bg-gray-50 flex flex-col items-center justify-center p-4 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
          <p className="text-sm text-red-500">Failed to load task columns</p>
        </div>
      ) : (
        <div
          ref={contentRef}
          className="flex-1 overflow-y-auto relative"
          style={{
            // Adjust for both header (4rem) and navigation bar if in native app
            height: isNativeApp
              ? `calc(100vh - 4rem - ${navigationBarHeight}px)`
              : 'calc(100vh - 4rem)',
          }}
        >
          {/* Swipe detection and content container */}
          <animated.div
            {...bind()}
            style={{
              height: '100%',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'pan-y',
              transform: x.to(x => `translateX(${x}px)`),
            }}
          >
            <MobileTaskColumn
              id={mobileActiveColumn}
              items={tasks
                .filter(item => item.status === mobileActiveColumn)
                .sort((a, b) => (a.position || 0) - (b.position || 0))}
              isCreating={isCreating}
              activeColumn={activeColumn}
              title={newTaskTitle}
              description={newTaskDescription}
              showDescription={showDescription}
              inputRef={inputRef}
              itemToDelete={itemToDelete}
              onDelete={handleTaskDeletion}
              onDeleteClick={setItemToDelete}
              onCancelDelete={() => setItemToDelete(null)}
              onUpdateTask={handleTaskUpdate}
              onTitleChange={setNewTaskTitle}
              onDescriptionChange={setNewTaskDescription}
              onSubmit={() => handleSubmit(mobileActiveColumn)}
              onCancel={() => {
                // Use the dedicated reset function to ensure all form state is cleared
                resetTaskFormState();
                setIsCreating(false);
              }}
              // Pass filter props to column for empty state handling
              showUnscheduledOnly={showUnscheduledOnly}
              showUnassignedOnly={showUnassignedOnly}
              userFilters={userFilters}
              hasCollaborators={hasCollaborators}
              availableTags={availableTags}
              // Pass React Query reorder callback
              onReorderTask={handleReorderTask}
            />
          </animated.div>
        </div>
      )}
    </div>
  );
}
